import { useRef, useState } from "react";
import { UploadCloud, X, GripVertical, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx";

export function MediaUploader({ media = [], onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of list) {
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/media", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || `Failed to upload ${file.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...media, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => onChange(media.filter((m) => m.id !== id));

  const onDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const next = [...media];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    onChange(next);
    setDragIndex(null);
  };

  return (
    <div className="space-y-3" data-testid="media-uploader">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid="media-upload-trigger"
        className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition-colors hover:border-ukf-gold hover:bg-ukf-canvas"
      >
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-ukf-red" /> : <UploadCloud className="h-6 w-6 text-slate-400" />}
        <span className="text-sm font-medium text-slate-600">
          {uploading ? "Uploading…" : "Click to upload media / documents"}
        </span>
        <span className="text-xs text-slate-400">JPG, PNG, WEBP, PDF, DOC, DOCX, XLS, XLSX · max 25MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        data-testid="media-file-input"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {media.length > 0 && (
        <ul className="space-y-2">
          {media.map((m, i) => (
            <li
              key={m.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              data-testid={`media-item-${m.id}`}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2"
            >
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-300" />
              {m.is_image ? (
                <img src={mediaUrl(m.id)} alt={m.filename} className="h-12 w-12 rounded object-cover" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded bg-slate-100">
                  <FileText className="h-5 w-5 text-slate-500" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-700">{m.filename}</p>
                <p className="text-xs text-slate-400">{(m.size / 1024).toFixed(0)} KB</p>
              </div>
              <button
                type="button"
                onClick={() => remove(m.id)}
                data-testid={`media-remove-${m.id}`}
                className="flex h-8 w-8 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-ukf-red"
                aria-label={`Remove ${m.filename}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
